import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  $isElementNode,
  $createParagraphNode,
  FORMAT_TEXT_COMMAND,
  type ElementNode,
  type LexicalEditor,
} from 'lexical';
import {
  $setBlocksType,
  $patchStyleText,
  $getSelectionStyleValueForProperty,
} from '@lexical/selection';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $createCodeNode } from '@lexical/code';
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_CHECK_LIST_COMMAND,
} from '@lexical/list';
import { TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import { useCallback, useState, useEffect, useRef } from 'react';
import { T, useGT } from 'gt-react';
import {
  TextBIcon,
  TextItalicIcon,
  TextStrikethroughIcon,
  CodeIcon,
  CodeBlockIcon,
  ListBulletsIcon,
  ListNumbersIcon,
  ListChecksIcon,
  QuotesIcon,
  TextTIcon,
  ImageIcon,
  SpinnerIcon,
  TextAaIcon,
  LinkIcon,
  TableIcon,
  MinusIcon,
  CaretDownIcon,
  ArrowsDownUpIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useInsertUploadedImage } from '@/components/common/lexical-image/useInsertUploadedImage';
import { sanitizeLinkUrl } from '@/components/common/lexical-links/sanitizeLinkUrl';

// Curated text-color palette. `value: null` clears the color back to default.
const TEXT_COLORS: { value: string | null; labelKey: string }[] = [
  { value: null, labelKey: 'Default' },
  { value: '#000000', labelKey: 'Black' },
  { value: '#5c5c5c', labelKey: 'Gray' },
  { value: '#b91c1c', labelKey: 'Red' },
  { value: '#c2410c', labelKey: 'Orange' },
  { value: '#a16207', labelKey: 'Yellow' },
  { value: '#15803d', labelKey: 'Green' },
  { value: '#0e7490', labelKey: 'Teal' },
  { value: '#1d4ed8', labelKey: 'Blue' },
  { value: '#6d28d9', labelKey: 'Purple' },
  { value: '#be185d', labelKey: 'Pink' },
  { value: '#ffffff', labelKey: 'White' },
];

// Web-safe font stacks. Custom web fonts don't load in Outlook/Gmail, so each
// option is a full fallback stack email clients can actually render. `value:
// null` clears back to the editor's default. The label is rendered in its own
// font so the menu previews the choice.
const FONT_FAMILIES: { value: string | null; label: string }[] = [
  { value: null, label: 'Default' },
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
  { value: '"Trebuchet MS", Helvetica, sans-serif', label: 'Trebuchet MS' },
  { value: 'Georgia, "Times New Roman", serif', label: 'Georgia' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: '"Courier New", Courier, monospace', label: 'Courier New' },
];

// Font sizes in px (email clients render px far more reliably than pt/em).
// `value: null` clears back to the editor's default.
const FONT_SIZES: { value: string | null; label: string }[] = [
  { value: null, label: 'Default' },
  { value: '10px', label: '10' },
  { value: '12px', label: '12' },
  { value: '14px', label: '14' },
  { value: '16px', label: '16' },
  { value: '18px', label: '18' },
  { value: '24px', label: '24' },
  { value: '36px', label: '36' },
];

// Unitless line-height multipliers applied to the selected paragraph(s).
// `value: null` clears back to the editor's default.
const LINE_SPACINGS: { value: string | null; label: string }[] = [
  { value: null, label: 'Default' },
  { value: '1', label: '1.0' },
  { value: '1.15', label: '1.15' },
  { value: '1.5', label: '1.5' },
  { value: '2', label: '2.0' },
];

// Read a single style property out of a node's stored cssText via a detached
// element, so parsing/merging matches how the browser normalizes styles.
function getStyleProperty(cssText: string, property: string): string {
  const el = document.createElement('div');
  el.style.cssText = cssText;
  return el.style.getPropertyValue(property);
}

/**
 * Optional toolbar controls. Defaults are tuned so the rich-text (HTML) editor,
 * which passes no `features`, keeps its existing set: text color, font family,
 * and font size on, everything markdown-specific off. Markdown editors flip
 * these to expose the controls whose output markdown can actually represent
 * (inline color/font styling can't survive markdown, so they stay off there).
 */
export interface ToolbarFeatureFlags {
  textColor?: boolean;
  fontFamily?: boolean;
  fontSize?: boolean;
  lineSpacing?: boolean;
  link?: boolean;
  strikethrough?: boolean;
  inlineCode?: boolean;
  codeBlock?: boolean;
  checklist?: boolean;
  table?: boolean;
  horizontalRule?: boolean;
}

export interface ToolbarPluginProps {
  /**
   * Enables an "insert image" button. Receives the picked file, uploads it,
   * and returns the rendered `src` plus the durable `documentId`. When omitted,
   * the image button is hidden.
   */
  uploadImage?: (file: File) => Promise<{ src: string; documentId: string }>;
  /** Toggle optional formatting controls. See {@link ToolbarFeatureFlags}. */
  features?: ToolbarFeatureFlags;
}

const DIVIDER = <div className="w-px h-6 bg-border mx-1" aria-hidden="true" />;

/** Insert / edit-link control. Owns its own popover + URL draft state. */
function LinkButton({
  editor,
  label,
  placeholder,
  removeLabel,
  applyLabel,
  isLink,
  linkUrl,
}: {
  editor: LexicalEditor;
  label: string;
  placeholder: string;
  removeLabel: string;
  applyLabel: string;
  isLink: boolean;
  linkUrl: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOpenChange = (next: boolean) => {
    if (next) setDraft(linkUrl);
    setOpen(next);
  };

  const apply = () => {
    // Drop disallowed schemes (javascript:, data:, …) before they reach a node.
    const safe = sanitizeLinkUrl(draft);
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, safe.length > 0 ? safe : null);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            'h-8 w-8 p-0',
            isLink && 'bg-accent text-accent-foreground',
          )}
          aria-pressed={isLink}
          aria-label={label}
          title={label}
        >
          <LinkIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-2"
        align="start"
        // Focus the URL field on open without an `autoFocus` attribute (which
        // jsx-a11y flags). Base UI
        // takes the target directly, where Radix needed the default suppressed
        // and the focus call made by hand.
        initialFocus={inputRef}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
          className="flex flex-col gap-2"
        >
          <input
            ref={inputRef}
            // `text`, not `url`: scheme-less input like `example.com` must reach
            // sanitizeLinkUrl (which upgrades it to https) instead of being
            // blocked by the browser's url validation.
            type="text"
            inputMode="url"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={placeholder}
            aria-label={label}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm outline-none transition-colors focus:border-ring focus:ring-[3px] focus:ring-ring/30"
          />
          <div className="flex items-center justify-end gap-2">
            {isLink && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive"
                onClick={() => {
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                  setOpen(false);
                }}
              >
                {removeLabel}
              </Button>
            )}
            <Button type="submit" size="sm">
              {applyLabel}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/**
 * WAI-ARIA toolbar keyboard pattern: the toolbar is a single tab stop, and
 * Arrow / Home / End move focus between its controls. Without this, a 15-control
 * toolbar puts 15 stops in the page's tab order before the editor content.
 */
function useRovingTabindex(toolbarRef: React.RefObject<HTMLDivElement | null>) {
  const getItems = useCallback(
    (): HTMLElement[] =>
      Array.from(
        toolbarRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled])',
        ) ?? [],
      ),
    [toolbarRef],
  );

  // Make exactly the first control tabbable on mount; the rest are arrow-reachable.
  useEffect(() => {
    getItems().forEach((el, index) => {
      el.tabIndex = index === 0 ? 0 : -1;
    });
  }, [getItems]);

  const setTabbable = (items: HTMLElement[], index: number) => {
    items.forEach((el, i) => {
      el.tabIndex = i === index ? 0 : -1;
    });
  };

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const items = getItems();
      const current = items.indexOf(document.activeElement as HTMLElement);
      if (current === -1) return;

      let next = current;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (current + 1) % items.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (current - 1 + items.length) % items.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = items.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      setTabbable(items, next);
      items[next]?.focus();
    },
    [getItems],
  );

  // Keep the tab stop on whichever control the user last focused (e.g. by click).
  const onFocus = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const items = getItems();
      const index = items.indexOf(event.target as HTMLElement);
      if (index !== -1) setTabbable(items, index);
    },
    [getItems],
  );

  return { onKeyDown, onFocus };
}

export function ToolbarPlugin({
  uploadImage,
  features,
}: ToolbarPluginProps = {}) {
  const flags: ToolbarFeatureFlags = {
    textColor: true,
    fontFamily: true,
    fontSize: true,
    lineSpacing: true,
    ...features,
  };
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const roving = useRovingTabindex(toolbarRef);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [currentColor, setCurrentColor] = useState('');
  const [currentFontFamily, setCurrentFontFamily] = useState('');
  const [currentFontSize, setCurrentFontSize] = useState('');
  const [currentLineHeight, setCurrentLineHeight] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const gt = useGT();
  const { insertImage, isUploading: isUploadingImage } =
    useInsertUploadedImage(uploadImage);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsCode(selection.hasFormat('code'));
      setCurrentColor(
        $getSelectionStyleValueForProperty(selection, 'color', ''),
      );
      setCurrentFontFamily(
        $getSelectionStyleValueForProperty(selection, 'font-family', ''),
      );
      setCurrentFontSize(
        $getSelectionStyleValueForProperty(selection, 'font-size', ''),
      );
      const block = selection.anchor.getNode().getTopLevelElement();
      setCurrentLineHeight(
        $isElementNode(block)
          ? getStyleProperty(block.getStyle(), 'line-height')
          : '',
      );

      const node = selection.anchor.getNode();
      const linkNode = $isLinkNode(node) ? node : node.getParent();
      if ($isLinkNode(linkNode)) {
        setIsLink(true);
        setLinkUrl(linkNode.getURL());
      } else {
        setIsLink(false);
        setLinkUrl('');
      }
    }
  }, []);

  // Apply (or clear, when `color` is null) the text color over the selection.
  const applyTextColor = (color: string | null) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { color });
      }
    });
  };

  // Apply (or clear, when null) an inline style property over the selection.
  // Used for font family and size, which are plain inline styles like color.
  const applyStyle = (property: string, value: string | null) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { [property]: value });
      }
    });
  };

  // Set line spacing on every block the selection touches. Block spacing lives
  // on the paragraph node's own style (not the inline text runs), so this walks
  // to each top-level block rather than using $patchStyleText.
  const applyLineSpacing = (value: string | null) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      const blocks = new Set<ElementNode>();
      for (const node of selection.getNodes()) {
        const block = node.getTopLevelElement();
        if ($isElementNode(block)) blocks.add(block);
      }
      for (const block of blocks) {
        const el = document.createElement('div');
        el.style.cssText = block.getStyle();
        if (value === null) el.style.removeProperty('line-height');
        else el.style.setProperty('line-height', value);
        block.setStyle(el.style.cssText);
      }
    });
  };

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatBold = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  };

  const formatItalic = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  };

  const formatStrikethrough = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
  };

  const formatInlineCode = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
  };

  const setBlock = (createNode: () => ElementNode) =>
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, createNode);
      }
    });

  const formatHeading = (headingSize: 'h1' | 'h2' | 'h3') =>
    setBlock(() => $createHeadingNode(headingSize));
  const formatParagraph = () => setBlock(() => $createParagraphNode());
  const formatQuote = () => setBlock(() => $createQuoteNode());
  const formatCodeBlock = () => setBlock(() => $createCodeNode());

  const formatBulletList = () => {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  };

  const formatNumberedList = () => {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  };

  const formatChecklist = () => {
    editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
  };

  const insertTable = () => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      columns: '3',
      rows: '3',
      includeHeaders: true,
    });
  };

  const insertHorizontalRule = () => {
    editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined);
  };

  const handleImageFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again re-triggers onChange.
    event.target.value = '';
    if (!file) return;
    await insertImage(file);
  };

  const defaultFontLabel = gt('Default', {
    context: 'default option in the font, size, and line-spacing menus',
  });
  const fontFamilyLabel = gt('Font', {
    context: 'toolbar button title for the font family menu',
  });
  const fontSizeLabel = gt('Font size', {
    context: 'toolbar button title for the font size menu',
  });
  const lineSpacingLabel = gt('Line spacing', {
    context: 'toolbar button title for the paragraph line-spacing menu',
  });
  // The stored style value is compared against our own option values, so an
  // exact match is expected; falls back to the Default label when unmatched.
  const activeFontFamily = FONT_FAMILIES.find(
    (font) => (font.value ?? '') === currentFontFamily,
  );
  const activeFontSize = FONT_SIZES.find(
    (size) => (size.value ?? '') === currentFontSize,
  );

  const boldLabel = gt('Bold', {
    context: 'toolbar button title for bold text formatting',
  });
  const italicLabel = gt('Italic', {
    context: 'toolbar button title for italic text formatting',
  });
  const strikethroughLabel = gt('Strikethrough', {
    context: 'toolbar button title for strikethrough text formatting',
  });
  const inlineCodeLabel = gt('Inline code', {
    context: 'toolbar button title for inline code formatting',
  });
  const bulletLabel = gt('Bullet list', {
    context: 'toolbar button title for bullet list formatting',
  });
  const numberedLabel = gt('Numbered list', {
    context: 'toolbar button title for numbered list formatting',
  });
  const checklistLabel = gt('Check list', {
    context: 'toolbar button title for check list formatting',
  });
  const quoteLabel = gt('Quote', {
    context: 'toolbar button title for blockquote formatting',
  });
  const codeBlockLabel = gt('Code block', {
    context: 'toolbar button title for code block formatting',
  });
  const linkLabel = gt('Link', {
    context: 'toolbar button title for inserting a link',
  });
  const tableLabel = gt('Insert table', {
    context: 'toolbar button title for inserting a table',
  });
  const hrLabel = gt('Divider', {
    context: 'toolbar button title for inserting a horizontal rule',
  });
  const imageLabel = gt('Insert image', {
    context: 'toolbar button title for inserting an image',
  });

  const showInsertGroup = flags.link || flags.table || flags.horizontalRule;

  return (
    <div
      ref={toolbarRef}
      role="toolbar"
      aria-label={gt('Formatting', { context: 'editor toolbar label' })}
      onKeyDown={roving.onKeyDown}
      onFocus={roving.onFocus}
      className="flex flex-wrap items-center gap-1 p-2 border-b border-border bg-muted"
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={formatBold}
        className={cn(
          'h-8 w-8 p-0',
          isBold && 'bg-accent text-accent-foreground',
        )}
        aria-pressed={isBold}
        aria-label={boldLabel}
        title={boldLabel}
      >
        <TextBIcon className="h-4 w-4" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={formatItalic}
        className={cn(
          'h-8 w-8 p-0',
          isItalic && 'bg-accent text-accent-foreground',
        )}
        aria-pressed={isItalic}
        aria-label={italicLabel}
        title={italicLabel}
      >
        <TextItalicIcon className="h-4 w-4" />
      </Button>

      {flags.strikethrough && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={formatStrikethrough}
          className={cn(
            'h-8 w-8 p-0',
            isStrikethrough && 'bg-accent text-accent-foreground',
          )}
          aria-pressed={isStrikethrough}
          aria-label={strikethroughLabel}
          title={strikethroughLabel}
        >
          <TextStrikethroughIcon className="h-4 w-4" />
        </Button>
      )}

      {flags.inlineCode && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={formatInlineCode}
          className={cn(
            'h-8 w-8 p-0',
            isCode && 'bg-accent text-accent-foreground',
          )}
          aria-pressed={isCode}
          aria-label={inlineCodeLabel}
          title={inlineCodeLabel}
        >
          <CodeIcon className="h-4 w-4" />
        </Button>
      )}

      {flags.textColor && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 flex flex-col items-center justify-center gap-0.5"
              aria-label={gt('Text color', {
                context: 'toolbar button title for text color',
              })}
              title={gt('Text color', {
                context: 'toolbar button title for text color',
              })}
            >
              <TextAaIcon className="h-4 w-4" />
              <span
                className="block h-1 w-4 rounded-sm border border-input"
                style={{ backgroundColor: currentColor || '#000000' }}
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <div className="grid grid-cols-6 gap-1">
              {TEXT_COLORS.map((color) => {
                const isActive =
                  (color.value ?? '').toLowerCase() ===
                  currentColor.toLowerCase();
                return (
                  <button
                    key={color.labelKey}
                    type="button"
                    onClick={() => applyTextColor(color.value)}
                    title={color.labelKey}
                    aria-label={color.labelKey}
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded border border-input',
                      isActive && 'ring-2 ring-ring ring-offset-1',
                    )}
                    style={
                      color.value ? { backgroundColor: color.value } : undefined
                    }
                  >
                    {color.value === null && (
                      <span className="text-[10px] font-medium text-muted-foreground">
                        A
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <input
                type="color"
                value={currentColor.startsWith('#') ? currentColor : '#000000'}
                onChange={(event) => applyTextColor(event.target.value)}
                className="h-6 w-6 cursor-pointer border-0 bg-transparent p-0"
              />
              <T context="custom text color option label">Custom…</T>
            </label>
          </PopoverContent>
        </Popover>
      )}

      {flags.fontFamily && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2 text-xs"
              aria-label={fontFamilyLabel}
              title={fontFamilyLabel}
            >
              <span
                className="max-w-[6rem] truncate"
                style={{ fontFamily: currentFontFamily || undefined }}
              >
                {activeFontFamily && activeFontFamily.value !== null
                  ? activeFontFamily.label
                  : defaultFontLabel}
              </span>
              <CaretDownIcon className="h-3 w-3 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-1" align="start">
            <div className="flex flex-col">
              {FONT_FAMILIES.map((font) => {
                const isActive = (font.value ?? '') === currentFontFamily;
                return (
                  <button
                    key={font.label}
                    type="button"
                    onClick={() => applyStyle('font-family', font.value)}
                    className={cn(
                      'flex items-center rounded px-2 py-1 text-left text-sm hover:bg-accent',
                      isActive && 'bg-accent text-accent-foreground',
                    )}
                    style={{ fontFamily: font.value ?? undefined }}
                  >
                    {font.value === null ? defaultFontLabel : font.label}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {flags.fontSize && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2 text-xs tabular-nums"
              aria-label={fontSizeLabel}
              title={fontSizeLabel}
            >
              <span>
                {activeFontSize && activeFontSize.value !== null
                  ? activeFontSize.label
                  : defaultFontLabel}
              </span>
              <CaretDownIcon className="h-3 w-3 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-20 p-1" align="start">
            <div className="flex flex-col">
              {FONT_SIZES.map((size) => {
                const isActive = (size.value ?? '') === currentFontSize;
                return (
                  <button
                    key={size.label}
                    type="button"
                    onClick={() => applyStyle('font-size', size.value)}
                    className={cn(
                      'flex items-center justify-center rounded px-2 py-1 text-sm tabular-nums hover:bg-accent',
                      isActive && 'bg-accent text-accent-foreground',
                    )}
                  >
                    {size.value === null ? defaultFontLabel : size.label}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {flags.lineSpacing && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2 text-xs"
              aria-label={lineSpacingLabel}
              title={lineSpacingLabel}
            >
              <ArrowsDownUpIcon className="h-4 w-4" />
              <CaretDownIcon className="h-3 w-3 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-28 p-1" align="start">
            <div className="flex flex-col">
              {LINE_SPACINGS.map((spacing) => {
                const isActive = (spacing.value ?? '') === currentLineHeight;
                return (
                  <button
                    key={spacing.label}
                    type="button"
                    onClick={() => applyLineSpacing(spacing.value)}
                    className={cn(
                      'flex items-center rounded px-2 py-1 text-left text-sm tabular-nums hover:bg-accent',
                      isActive && 'bg-accent text-accent-foreground',
                    )}
                  >
                    {spacing.value === null ? defaultFontLabel : spacing.label}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {DIVIDER}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={formatParagraph}
        className="h-8 px-2 text-xs"
        aria-label={gt('Paragraph', {
          context: 'toolbar button title for paragraph text formatting',
        })}
        title={gt('Paragraph', {
          context: 'toolbar button title for paragraph text formatting',
        })}
      >
        <TextTIcon className="h-4 w-4 mr-1" />P
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => formatHeading('h1')}
        className="h-8 px-2 text-xs font-bold"
        title={gt('Heading 1', {
          context: 'toolbar button title for heading 1 text formatting',
        })}
      >
        <T context="toolbar button label for heading 1">H1</T>
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => formatHeading('h2')}
        className="h-8 px-2 text-xs font-bold"
        title={gt('Heading 2', {
          context: 'toolbar button title for heading 2 text formatting',
        })}
      >
        <T context="toolbar button label for heading 2">H2</T>
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => formatHeading('h3')}
        className="h-8 px-2 text-xs font-bold"
        title={gt('Heading 3', {
          context: 'toolbar button title for heading 3 text formatting',
        })}
      >
        <T context="toolbar button label for heading 3">H3</T>
      </Button>

      {DIVIDER}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={formatBulletList}
        className="h-8 w-8 p-0"
        aria-label={bulletLabel}
        title={bulletLabel}
      >
        <ListBulletsIcon className="h-4 w-4" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={formatNumberedList}
        className="h-8 w-8 p-0"
        aria-label={numberedLabel}
        title={numberedLabel}
      >
        <ListNumbersIcon className="h-4 w-4" />
      </Button>

      {flags.checklist && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={formatChecklist}
          className="h-8 w-8 p-0"
          aria-label={checklistLabel}
          title={checklistLabel}
        >
          <ListChecksIcon className="h-4 w-4" />
        </Button>
      )}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={formatQuote}
        className="h-8 w-8 p-0"
        aria-label={quoteLabel}
        title={quoteLabel}
      >
        <QuotesIcon className="h-4 w-4" />
      </Button>

      {flags.codeBlock && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={formatCodeBlock}
          className="h-8 w-8 p-0"
          aria-label={codeBlockLabel}
          title={codeBlockLabel}
        >
          <CodeBlockIcon className="h-4 w-4" />
        </Button>
      )}

      {showInsertGroup && DIVIDER}

      {flags.link && (
        <LinkButton
          editor={editor}
          label={linkLabel}
          placeholder={gt('https://example.com', {
            context: 'link url input placeholder',
          })}
          removeLabel={gt('Remove', { context: 'remove link button' })}
          applyLabel={gt('Apply', { context: 'apply link button' })}
          isLink={isLink}
          linkUrl={linkUrl}
        />
      )}

      {flags.table && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={insertTable}
          className="h-8 w-8 p-0"
          aria-label={tableLabel}
          title={tableLabel}
        >
          <TableIcon className="h-4 w-4" />
        </Button>
      )}

      {flags.horizontalRule && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={insertHorizontalRule}
          className="h-8 w-8 p-0"
          aria-label={hrLabel}
          title={hrLabel}
        >
          <MinusIcon className="h-4 w-4" />
        </Button>
      )}

      {uploadImage && (
        <>
          {DIVIDER}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isUploadingImage}
            onClick={() => fileInputRef.current?.click()}
            className="h-8 w-8 p-0"
            aria-label={imageLabel}
            title={imageLabel}
          >
            {isUploadingImage ? (
              <SpinnerIcon className="h-4 w-4 animate-spin" />
            ) : (
              <ImageIcon className="h-4 w-4" />
            )}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            className="hidden"
            onChange={handleImageFileSelected}
          />
        </>
      )}
    </div>
  );
}
